import { API_CONFIG } from '../lib/apiConfig';
import { parseCsv } from '../lib/csv';
import { createCsvStatsHook } from '../lib/hookFactories';
import { FoClass, FoStats } from '../lib/types';
import { FO_PALETTE } from '../lib/foColors';

// CSV schema (single row, wide format):
//   unknown_forest_acres,non_forest_acres,water_acres,family_acres,corporate_acres,tribal_acres,federal_acres,state_acres,local_acres
//
// Acres are computed in EPSG:5070 (CONUS Albers) upstream so they're accurate
// across the full CONUS footprint. forestAcres is the sum of all seven
// ownership classes; non_forest_acres and water_acres are context totals for
// the stat card grid (NOT pie slices).
const CLASS_COL: Record<FoClass, string> = {
  unknown_forest: 'unknown_forest_acres',
  family:         'family_acres',
  corporate:      'corporate_acres',
  tribal:         'tribal_acres',
  federal:        'federal_acres',
  state:          'state_acres',
  local:          'local_acres',
};

const parseFoStats = (text: string): FoStats | null => {
  const rows = parseCsv(text);
  if (rows.length === 0) return null;
  const row = rows[0];

  const classAcres = FO_PALETTE.order.reduce((acc, cls) => {
    const v = Number(row[CLASS_COL[cls]]);
    acc[cls] = Number.isFinite(v) ? v : 0;
    return acc;
  }, {} as Record<FoClass, number>);

  const forestAcres = FO_PALETTE.order.reduce((s, cls) => s + classAcres[cls], 0);
  const nonForestAcres = Number(row['non_forest_acres']);
  const waterAcres = Number(row['water_acres']);

  return {
    classAcres,
    forestAcres,
    nonForestAcres: Number.isFinite(nonForestAcres) ? nonForestAcres : 0,
    waterAcres: Number.isFinite(waterAcres) ? waterAcres : 0,
  };
};

/**
 * Fetches Forest Ownership zonal stats for the supply shed defined by
 * (millCode, driveTime). CSV lives at
 * sfi/forest_ownership/{mill}_{dt}min_forest_ownership.csv.
 */
export const useForestOwnership = createCsvStatsHook({
  path: API_CONFIG.s3.paths.foStats,
  parse: parseFoStats,
  errorPrefix: 'Failed to load forest ownership data',
});
